import { withEntityTitle } from '@/components/workflows/docProvenance';
import type { SigningMode } from '@/components/workflows/signingModel';
import { sealBlockHtml, SEAL_STYLE_CSS } from '@/components/workflows/sealModel';
import {
  configNeedsResidentialLease,
  costModeOf,
  DIRECTION_OF_PAYMENT_TEACHING,
  type HousingWorkflowData,
} from './housingContent';

/**
 * DRAFT document builders for the "Establish the Center and Occupancy" workflow.
 *
 * Every paper here is produced as a clearly marked DRAFT and needs founder
 * approval before this workflow goes live. The designation vocabulary is
 * "place of stewardship observance" throughout.
 */

export const HOUSING_FORMATS_APPROVED = true;

function esc(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function longDate(value?: string): string {
  const d = value ? new Date(value) : new Date();
  const safe = Number.isNaN(d.getTime()) ? new Date() : d;
  return safe.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

const BASE_STYLE = `
  @page { size: letter; margin: 1in; }
  body { font-family: Cambria, Georgia, serif; font-size: 12pt; line-height: 1.6; color: #1a1a1a; }
  h1 { font-size: 16pt; text-align: center; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 4px; }
  h2 { font-size: 12pt; text-transform: uppercase; letter-spacing: 0.05em; margin-top: 26px; }
  .subtitle { text-align: center; font-style: italic; margin-bottom: 26px; }
  .draft { border: 1px solid #8a6d3b; background: #fdf6e3; color: #6b5320; padding: 10px 14px; margin-bottom: 22px; font-size: 10pt; text-align: center; letter-spacing: 0.04em; }
  table { width: 100%; border-collapse: collapse; margin-top: 12px; font-size: 11pt; }
  th, td { border: 1px solid #bbb; padding: 8px 10px; text-align: left; vertical-align: top; }
  th { background: #f3f1ec; font-weight: bold; }
  .sig { margin-top: 46px; }
  .sig-line { border-bottom: 1px solid #333; width: 320px; height: 34px; margin-bottom: 4px; }
  .sig-label { font-size: 10pt; }
  ol li, ul li { margin-bottom: 8px; }
  .note { font-size: 10pt; font-style: italic; color: #444; margin-top: 26px; }
  ${SEAL_STYLE_CSS}
`;

function page(title: string, subtitle: string, inner: string): string {
  return `<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"><title>${esc(title)}</title><style>${BASE_STYLE}</style></head>
<body>
  <div class="draft">DRAFT format. This wording has not yet been approved. Do not sign this page until the approved format is in place.</div>
  <h1>${esc(title)}</h1>
  <p class="subtitle">${esc(subtitle)}</p>
  ${inner}
  <p class="note">Sign this before anyone moves in and before any money moves. The date on it is the true date it is signed.</p>
</body>
</html>`;
}

export interface HousingBuildContext {
  data: HousingWorkflowData;
  associationName: string;
  associationOfficerRole: string;
  date?: string;
  /** True when the Association keeps a seal and the steward applied it. */
  sealEnabled?: boolean;
  /** The rendered seal markup. */
  sealSvg?: string;
}

function tenantName(ctx: HousingBuildContext): string {
  return esc(ctx.data.entity_legal_name || ctx.associationName || '[Entity legal name]');
}

function portionsTable(data: HousingWorkflowData): string {
  const rows = (data.portions || [])
    .filter((p) => p.name.trim())
    .map(
      (p) => `<tr><td>${esc(p.name)}</td><td>${esc(p.square_feet)}</td><td>${esc(p.use)}</td></tr>`,
    )
    .join('');
  if (!rows) return '<p>[List the rooms that are leased.]</p>';
  return `<table><thead><tr><th>Room or area</th><th>Square feet</th><th>What happens there</th></tr></thead><tbody>${rows}</tbody></table>`;
}

function commonUseTable(data: HousingWorkflowData): string {
  const rows = (data.common_uses || [])
    .filter((c) => c.area.trim())
    .map((c) => `<tr><td>${esc(c.area)}</td><td>${esc(c.schedule)}</td></tr>`)
    .join('');
  if (!rows) return '<p>[List the shared areas and when they are used.]</p>';
  return `<table><thead><tr><th>Shared area</th><th>When it is used</th></tr></thead><tbody>${rows}</tbody></table>`;
}

function utilitiesTable(data: HousingWorkflowData): string {
  const rows = (data.utilities || [])
    .filter((u) => u.item.trim())
    .map((u) => `<tr><td>${esc(u.item)}</td><td>${esc(u.who_pays)}</td><td>${esc(u.method)}</td></tr>`)
    .join('');
  if (!rows) return '<p>[List each cost, who pays it, and how.]</p>';
  return `<table><thead><tr><th>Cost</th><th>Who pays it</th><th>Paid in whose name, or how it is split</th></tr></thead><tbody>${rows}</tbody></table>`;
}

/** Partial Premises Lease, Configuration A. */
export function buildPartialPremisesLeaseHtml(ctx: HousingBuildContext): string {
  const { data } = ctx;
  const dateStr = longDate(ctx.date || data.start_date);
  const inner = `
  <p>This lease is made on ${esc(dateStr)} between ${esc(
    data.owner_name || '[Owner name]',
  )}, who owns the property at ${esc(data.property_address || '[Property address]')} and is the landlord here, and ${tenantName(
    ctx,
  )}, which is the tenant here.</p>
  <h2>What is leased</h2>
  <p>Only the parts named below are leased. The rest of the property stays with the household, which lives there as the owner. No occupancy paper is needed for the rest.</p>
  ${portionsTable(data)}
  <h2>Shared areas and when they are used</h2>
  ${commonUseTable(data)}
  <h2>Term</h2>
  <p>The term starts on ${esc(longDate(data.start_date))} and runs for ${esc(
    data.term_months || '12',
  )} months. It renews only in writing.</p>
  <h2>Rent for the leased portion</h2>
  <p>The tenant pays ${esc(
    data.portion_rent || '[amount]',
  )} each month for the leased portion. That number is the fair rental value of that portion, and the basis for it is written in the fair rental value worksheet that goes with this lease.</p>
  ${
    costModeOf(data) === 'inclusive'
      ? '<p>The rent covers everything. It includes the costs of the leased portion, so the tenant pays that one number each month and nothing else. Household bills stay with the household.</p>'
      : ''
  }
  <h2>What the tenant is responsible for</h2>
  <p>${esc(data.entity_responsibilities || '[State what the tenant takes care of.]')}</p>
  ${
    costModeOf(data) === 'inclusive'
      ? '<p>There is no separate cost schedule with this lease, because the rent is inclusive.</p>'
      : '<p>Costs are handled as written in the utilities and costs schedule that goes with this lease.</p>'
  }
  <div class="sig">
    <div class="sig-line"></div>
    <div class="sig-label">${esc(data.owner_name || '[Owner name]')}, owner<br/>Date: ${esc(dateStr)}</div>
  </div>
  <div class="sig">
    <div class="sig-line"></div>
    <div class="sig-label">${esc(data.entity_signer_name || '[Signer name]')}, ${esc(
      data.entity_signer_capacity || '[capacity]',
    )}<br/>${tenantName(ctx)}<br/>Date: ${esc(dateStr)}</div>
  </div>`;
  return page('Partial Premises Lease', 'Only the named portions are leased', `${inner}${sealBlockHtml(ctx)}`);
}

/** Fair Rental Value Study Worksheet. */
export function buildFrvWorksheetHtml(ctx: HousingBuildContext, scope: 'portion' | 'residential'): string {
  const { data } = ctx;
  const inner = `
  <p>Property: ${esc(data.property_address || '[Property address]')}</p>
  <p>What this worksheet prices: ${
    scope === 'portion'
      ? 'the leased portion only, meaning the named rooms and the scheduled use of shared areas.'
      : 'the residential portion the household lives in.'
  }</p>
  <h2>Whole property fair rental value</h2>
  <p>${esc(data.frv_whole_property || '[amount per month]')}</p>
  <h2>Where that number came from</h2>
  <p>${esc(data.frv_basis || '[Say where the number came from.]')}</p>
  <p>${esc(data.frv_sources || '[List the listings, the appraisal, or the written opinion, and attach copies.]')}</p>
  <h2>How the portion was figured</h2>
  <p>${esc(data.frv_portion_method || '[Show the math: square feet, hours of use, or both.]')}</p>
  <h2>The number this worksheet supports</h2>
  <p>${esc((scope === 'portion' ? data.portion_rent : data.residential_rent) || '[amount per month]')} each month.</p>
  <p>Is this number rent-inclusive: ${
    costModeOf(data) === 'inclusive'
      ? 'Yes. The rent covers everything, so this number carries the costs of the space as well.'
      : 'No. Certain costs are paid separately, by the written split in the utilities and costs schedule.'
  }</p>
  <p class="note">Refresh this once a year and keep the old copies. The attachments are part of the record.</p>
  <div class="sig">
    <div class="sig-line"></div>
    <div class="sig-label">Prepared by<br/>Date:</div>
  </div>`;
  return page('Fair Rental Value Study Worksheet', scope === 'portion' ? 'The leased portion' : 'The residential portion', inner);
}

/** Utilities and Costs Allocation Schedule. */
export function buildUtilitiesScheduleHtml(ctx: HousingBuildContext): string {
  const { data } = ctx;
  const inner = `
  <p>This schedule goes with the lease for ${esc(data.property_address || '[Property address]')}.</p>
  <p>Anything the entity pays is paid in the entity's own name, or split by a written method that is kept with this page. The entity does not pay a household bill just because it is easier.</p>
  ${utilitiesTable(data)}
  <p class="note">Keep the statements and the payment records with this schedule.</p>
  <div class="sig">
    <div class="sig-line"></div>
    <div class="sig-label">${esc(data.entity_signer_name || '[Signer name]')}, ${esc(
      data.entity_signer_capacity || '[capacity]',
    )}<br/>${tenantName(ctx)}</div>
  </div>`;
  return page('Utilities and Costs Allocation Schedule', 'What the entity pays and how', `${inner}${sealBlockHtml(ctx)}`);
}

/** Optional Direction of Payment Addendum. */
export function buildDirectionOfPaymentHtml(ctx: HousingBuildContext): string {
  const { data } = ctx;
  const dateStr = longDate(ctx.date || data.start_date);
  const inner = `
  <p>This page is an addendum to the Partial Premises Lease for ${esc(
    data.property_address || '[Property address]',
  )}.</p>
  <h2>The direction</h2>
  <p>${esc(data.owner_name || '[Owner name]')} directs ${tenantName(ctx)} to send the monthly rent for the leased portion to ${esc(
    data.lender_name || '[Lender name]',
  )} instead of sending it to the owner.</p>
  <h2>What this does not change</h2>
  <p>${esc(DIRECTION_OF_PAYMENT_TEACHING)}</p>
  <div class="sig">
    <div class="sig-line"></div>
    <div class="sig-label">${esc(data.owner_name || '[Owner name]')}, owner<br/>Date: ${esc(dateStr)}</div>
  </div>
  <div class="sig">
    <div class="sig-line"></div>
    <div class="sig-label">${esc(data.entity_signer_name || '[Signer name]')}, ${esc(
      data.entity_signer_capacity || '[capacity]',
    )}<br/>${tenantName(ctx)}<br/>Date: ${esc(dateStr)}</div>
  </div>`;
  return page('Direction of Payment Addendum', 'Optional, and it changes nothing about what the rent is', `${inner}${sealBlockHtml(ctx)}`);
}

/** Mortgage Compliance Memorandum. */
export function buildMortgageMemoHtml(ctx: HousingBuildContext): string {
  const { data } = ctx;
  const inner = `
  <p>Property: ${esc(data.property_address || '[Property address]')}</p>
  <p>Lender: ${esc(data.mortgage_lender || '[Lender name]')}</p>
  <h2>What was checked</h2>
  <ul>
    <li>The loan papers were read for anything about renting out part of the home.</li>
    <li>The owner still lives in the home, and title has not moved.</li>
    <li>Only named portions are leased, and the household keeps the rest.</li>
    <li>The loan payment keeps being made on time and in full.</li>
  </ul>
  <h2>What the papers say</h2>
  <p>${esc(data.mortgage_notes || '[Write what the loan papers actually say about this, and quote the section.]')}</p>
  <h2>What happens next</h2>
  <p>If anything in the loan papers is unclear, ask the lender in writing and keep the answer with this memo. If the lender says something matters here, stop and get help before the lease starts.</p>
  <div class="sig">
    <div class="sig-line"></div>
    <div class="sig-label">${esc(data.owner_name || '[Owner name]')}, owner<br/>Date:</div>
  </div>`;
  return page('Mortgage Compliance Memorandum', 'What the loan papers say about this arrangement', `${inner}${sealBlockHtml(ctx)}`);
}

/** Designation Resolution. */
export function buildDesignationResolutionHtml(ctx: HousingBuildContext): string {
  const { data, associationName } = ctx;
  const dateStr = longDate(ctx.date);
  const scope =
    data.config === 'A'
      ? 'the leased portion described in the Partial Premises Lease of the same date'
      : 'the property';
  const inner = `
  <p>${esc(associationName)}, acting through its ${esc(
    ctx.associationOfficerRole,
  )}, takes the following action on ${esc(dateStr)}.</p>
  <h2>Action taken</h2>
  <ol>
    <li>${scope.charAt(0).toUpperCase()}${scope.slice(1)} at ${esc(
      data.property_address || '[Property address]',
    )} is designated a place of stewardship observance of ${esc(associationName)}.</li>
    <li>It is kept and used for the purposes of ${esc(associationName)}, and its use is written down in the gathering and use log.</li>
    <li>Gatherings are held there ${esc(data.gathering_cadence || '[state how often]')}.</li>
    ${data.designation_notes ? `<li>${esc(data.designation_notes)}</li>` : ''}
  </ol>
  <div class="sig">
    <div class="sig-line"></div>
    <div class="sig-label">${esc(data.association_officer_name || '[Officer name]')}, ${esc(
      ctx.associationOfficerRole,
    )}<br/>${esc(associationName)}<br/>Date: ${esc(dateStr)}</div>
  </div>`;
  return page('Designation of a Place of Stewardship Observance', `Resolution of ${associationName}`, `${inner}${sealBlockHtml(ctx)}`);
}

/** Gathering and Use Log. */
export function buildUseLogHtml(ctx: HousingBuildContext): string {
  const { data } = ctx;
  const blank = Array.from({ length: 12 })
    .map(() => '<tr><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td></tr>')
    .join('');
  const inner = `
  <p>Place: ${esc(data.property_address || '[Property address]')}</p>
  <p>Write one line each time the place is used for the purposes of the organization. Fill this in as it happens, not at the end of the year.</p>
  <table>
    <thead><tr><th>Date</th><th>What was held</th><th>Which rooms</th><th>How many came</th><th>Who led it</th></tr></thead>
    <tbody>${blank}</tbody>
  </table>
  <p class="note">Add a page whenever this one fills up. Keep them all together.</p>`;
  return page('Gathering and Use Log', 'One line for every gathering', inner);
}

function scheduleEInner(ctx: HousingBuildContext): string {
  const { data } = ctx;
  return `
  <p>Property: ${esc(data.property_address || '[Property address]')}</p>
  <p>Keep these together in one folder for the year, so the person who prepares your return has everything in one place.</p>
  <ol>
    <li>The signed Partial Premises Lease and any addendum.</li>
    <li>The fair rental value worksheet with its attachments, and last year's copy.</li>
    <li>The utilities and costs allocation schedule, with statements and payment records.</li>
    <li>Every rent payment record for the year, showing the date and the amount.</li>
    <li>The mortgage compliance memorandum and anything the lender wrote back.</li>
    <li>The designation resolution.</li>
    <li>The gathering and use log for the year.</li>
    <li>Repair and upkeep receipts for the leased portion.</li>
  </ol>
  <h2>Two plain lines to remember</h2>
  <ul>
    <li>Even if the money in and the money out come out even, the rent still gets reported. Breakeven is still reported.</li>
    <li>A licensed professional prepares the return. This folder is what you hand them.</li>
  </ul>`;
}

/** Schedule E Support File Checklist, used as the closing page of the year packet. */
export function buildScheduleEChecklistHtml(ctx: HousingBuildContext): string {
  return page('Schedule E Support File Checklist', 'What to keep for the year', scheduleEInner(ctx));
}

/** Residential Portion Lease, Configuration C with a household living there. */
export function buildResidentialLeaseHtml(ctx: HousingBuildContext): string {
  const { data } = ctx;
  const dateStr = longDate(ctx.date || data.start_date);
  const inner = `
  <p>This lease is made on ${esc(dateStr)} between ${tenantName(
    ctx,
  )}, which owns the property at ${esc(
    data.property_address || '[Property address]',
  )} and is the landlord here, and ${esc(data.owner_name || '[Household member name]')}, who is the tenant here.</p>
  <h2>What is leased</h2>
  <p>${esc(data.residential_portion || '[Describe the part of the property the household lives in.]')}</p>
  <h2>Term</h2>
  <p>The term starts on ${esc(longDate(data.start_date))} and runs for ${esc(
    data.term_months || '12',
  )} months. It renews only in writing.</p>
  <h2>Rent</h2>
  <p>The tenant pays ${esc(
    data.residential_rent || '[amount]',
  )} each month to the landlord. That is the fair rental value of the residential portion, and the basis for it is in the fair rental value worksheet that goes with this lease. The rent is actually paid, on time, from the household's own funds.</p>
  <div class="sig">
    <div class="sig-line"></div>
    <div class="sig-label">${esc(data.entity_signer_name || '[Signer name]')}, ${esc(
      data.entity_signer_capacity || '[capacity]',
    )}<br/>${tenantName(ctx)}<br/>Date: ${esc(dateStr)}</div>
  </div>
  <div class="sig">
    <div class="sig-line"></div>
    <div class="sig-label">${esc(data.owner_name || '[Household member name]')}, tenant<br/>Date: ${esc(dateStr)}</div>
  </div>`;
  return page('Residential Portion Lease', 'The household pays fair value to the entity', `${inner}${sealBlockHtml(ctx)}`);
}

/** Written self serving test record, Configuration C. */
export function buildSelfServingTestHtml(ctx: HousingBuildContext): string {
  const { data } = ctx;
  const inner = `
  <p>Property: ${esc(data.property_address || '[Property address]')}</p>
  <h2>Read by a stranger, this arrangement would look like</h2>
  <p>${esc(data.self_serving_test || '[Write the sentence in your own words.]')}</p>
  <p class="note">Write it once a year, in your own words, and keep the old ones.</p>
  <div class="sig">
    <div class="sig-line"></div>
    <div class="sig-label">${esc(data.association_officer_name || '[Officer name]')}, ${esc(
      ctx.associationOfficerRole,
    )}<br/>Date:</div>
  </div>`;
  return page('Written Self Serving Test Record', 'How this reads to someone outside', `${inner}${sealBlockHtml(ctx)}`);
}

function dutiesAttestationInner(ctx: HousingBuildContext): string {
  const { data, associationName } = ctx;
  return `
  <p>This page is signed once a year by the person who carries the stewardship duties at ${esc(
    data.property_address || '[Property address]',
  )}.</p>
  <h2>The duties I carry</h2>
  <ul>
    <li>I keep the accounts and records of ${esc(associationName)} in order.</li>
    <li>I keep the governance of the organization in order, including its resolutions and minutes.</li>
    <li>I care for what the organization holds, including this place.</li>
    <li>I form and teach the people of the organization.</li>
    <li>I convene the community and keep the gathering and use log current.</li>
  </ul>
  <h2>What I affirm</h2>
  <ul>
    <li>The paper and the practice match. What is written here is what actually happens.</li>
    <li>The place is used as a place of stewardship observance, and the log shows it.</li>
    <li>Any rent that is owed has actually been paid, on time, in the direction the papers say.</li>
  </ul>
  <div class="sig">
    <div class="sig-line"></div>
    <div class="sig-label">${esc(data.association_officer_name || '[Name]')}<br/>Date:</div>
  </div>`;
}

/** Annual Stewardship Duties Attestation, duties stated as stewardship functions only. */
export function buildDutiesAttestationHtml(ctx: HousingBuildContext): string {
  return page('Annual Stewardship Duties Attestation', 'Signed once a year', `${dutiesAttestationInner(ctx)}${sealBlockHtml(ctx)}`);
}

/**
 * Housing Year Packet. One combined document a member downloads once a year and
 * hands to whoever prepares the return.
 */
export function buildAnnualPacketHtml(ctx: HousingBuildContext): string {
  const { data } = ctx;
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  const rentRows = months
    .map((m) => `<tr><td>${m}</td><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td></tr>`)
    .join('');

  const inner = `
  <h2>What is inside</h2>
  <ol>
    <li>This cover page.</li>
    <li>The yearly attestation page, ready to sign.</li>
    <li>The twelve month rent confirmation table.</li>
    <li>The one page fair rental value confirmation.</li>
    <li>The Schedule E support file checklist.</li>
  </ol>
  <p>Place: ${esc(data.property_address || '[Property address]')}</p>
  <p>This is the whole yearly sitting. It takes about twenty minutes.</p>

  <h1 style="margin-top:48px">Annual Stewardship Duties Attestation</h1>
  ${dutiesAttestationInner(ctx)}

  <h1 style="margin-top:48px">Rent Confirmation, Twelve Months</h1>
  <p>Glance down your bank lines and confirm each month.</p>
  <table>
    <thead><tr><th>Month</th><th>Paid on</th><th>Amount</th><th>Initials</th></tr></thead>
    <tbody>${rentRows}</tbody>
  </table>

  <h1 style="margin-top:48px">Fair Rental Value Confirmation</h1>
  <p>Last year's number: ${esc(data.portion_rent || '___')} each month.</p>
  <p>This year: same, or a new number of ___ because ___, with one fresh comparable attached.</p>
  <div class="sig">
    <div class="sig-line"></div>
    <div class="sig-label">Confirmed by<br/>Date:</div>
  </div>

  <h1 style="margin-top:48px">Schedule E Support File Checklist</h1>
  ${scheduleEInner(ctx)}`;

  return page('Housing Year Packet', 'One sitting a year, all in one place', inner);
}

export interface HousingOutput {
  key: string;
  title: string;
  requiresSignature: boolean;
  signing: SigningMode;
  defaultInk?: boolean;
  signingReason?: string;
  html: string;
}

/**
 * The one housing paper an outside party takes in original form. The party who
 * sends the money asks for the signed original before it changes where the
 * money goes.
 */
export const DIRECTION_OF_PAYMENT_INK_REASON =
  'The party receiving the payment asks for the original signed page before it changes where the money goes.';

function buildHousingOutputsInternal(ctx: HousingBuildContext): HousingOutput[] {
  const { data } = ctx;
  const outputs: HousingOutput[] = [];

  const add = (key: string, title: string, html: string, requiresSignature: boolean, wetRequired = false) =>
    outputs.push(
      wetRequired
        ? {
            key,
            title,
            html,
            requiresSignature,
            signing: 'wet_required' as SigningMode,
            signingReason: DIRECTION_OF_PAYMENT_INK_REASON,
          }
        : { key, title, html, requiresSignature, signing: 'stewards_choice' as SigningMode, defaultInk: false },
    );

  if (data.config === 'A') {
    add('housing_partial_premises_lease', 'Partial Premises Lease', buildPartialPremisesLeaseHtml(ctx), true);
    add(
      'housing_frv_worksheet',
      'Fair Rental Value Study Worksheet',
      buildFrvWorksheetHtml(ctx, 'portion'),
      false,
    );
    if (costModeOf(data) === 'itemized') {
      add(
        'housing_utilities_allocation',
        'Utilities and Costs Allocation Schedule',
        buildUtilitiesScheduleHtml(ctx),
        true,
      );
    }
    if (data.direction_of_payment) {
      add(
        'housing_direction_of_payment',
        'Direction of Payment Addendum',
        buildDirectionOfPaymentHtml(ctx),
        true,
        true,
      );
    }
    if (data.has_mortgage) {
      add(
        'housing_mortgage_memo',
        'Mortgage Compliance Memorandum',
        buildMortgageMemoHtml(ctx),
        true,
      );
    }
    add(
      'housing_designation_resolution',
      'Designation of a Place of Stewardship Observance',
      buildDesignationResolutionHtml(ctx),
      true,
    );
    add('housing_use_log', 'Gathering and Use Log', buildUseLogHtml(ctx), false);
  }

  if (data.config === 'C' || data.config === 'D') {
    add(
      'housing_designation_resolution',
      'Designation of a Place of Stewardship Observance',
      buildDesignationResolutionHtml(ctx),
      true,
    );
    add('housing_use_log', 'Gathering and Use Log', buildUseLogHtml(ctx), false);
  }

  if (configNeedsResidentialLease(data)) {
    add('housing_residential_lease', 'Residential Portion Lease', buildResidentialLeaseHtml(ctx), true);
    add(
      'housing_frv_worksheet_residential',
      'Fair Rental Value Study Worksheet',
      buildFrvWorksheetHtml(ctx, 'residential'),
      false,
    );
    add(
      'housing_self_serving_test',
      'Written Self Serving Test Record',
      buildSelfServingTestHtml(ctx),
      true,
    );
  }

  if (data.config === 'A' || data.config === 'C' || data.config === 'D') {
    add(
      'housing_duties_attestation',
      'Annual Stewardship Duties Attestation',
      buildDutiesAttestationHtml(ctx),
      true,
    );
    add('housing_year_packet', 'Housing Year Packet', buildAnnualPacketHtml(ctx), false);
  }

  return outputs;
}

/**
 * Every generated paper carries only the entity's identity. The HTML title
 * becomes "Document title, Entity name" so the file and the PDF metadata name
 * the Association, not the portal.
 */
export function buildHousingOutputs(ctx: HousingBuildContext): HousingOutput[] {
  const entityName = ctx.data.entity_legal_name || ctx.associationName;
  return buildHousingOutputsInternal(ctx).map((output) => ({ ...output, html: withEntityTitle(output.html, entityName) }));
}
